import React, { createContext, useContext, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { RESCUE_LOGIN_PENDING_TRACK_KEY, setRescueAnalyticsContext, trackLogin } from '../analytics';
import { rescueOrgInfoQuery, RescueOrgInfoData, RescueOrgShippingAddress } from './rescueQueries';
import { identifyUser } from '../../lib/analytics/events';
import { AppState } from '../../lib/store';

interface RescueContextValue {
  orgName: string;
  shippingAddress: RescueOrgShippingAddress | null;
  isAdmin: boolean;
  isVerifiedReseller: boolean;
  /** True once the user has a chip reseller account, whether verified or still pending review. */
  isReseller: boolean;
  /**
   * True while `rescueOrgInfoQuery` is in flight. Callers gating page render
   * on `isVerifiedReseller` must also check this — `verified` defaults to
   * `false` during loading, which would otherwise flash a partner-gate view
   * to verified users on cold-load entry to a gated page.
   */
  orgInfoLoading: boolean;
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const RescueContext = createContext<RescueContextValue>({
  orgName: '',
  shippingAddress: null,
  isAdmin: false,
  isVerifiedReseller: false,
  isReseller: false,
  orgInfoLoading: false,
  sidebarOpen: false,
  setSidebarOpen: () => {},
});

export function useRescueContext() {
  return useContext(RescueContext);
}

function resolveOrgName(data?: RescueOrgInfoData): string {
  if (!data) {
    return '';
  }
  const resellerName = data.currentUser?.chipReseller?.name;
  if (resellerName) {
    return resellerName;
  }
  const rescueHousehold = data.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
  return rescueHousehold?.household.name ?? '';
}

function resolveOrgId(data?: RescueOrgInfoData): string {
  if (!data) {
    return '';
  }
  const resellerId = data.currentUser?.chipReseller?.id;
  if (resellerId) {
    return resellerId;
  }
  const rescueHousehold = data.currentUser?.userHouseholds?.find((uh) => uh.household.type === 'RESCUE');
  return rescueHousehold?.household.id ?? '';
}

export function RescueContextProvider({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const session = useSelector((state: AppState) => state.session);
  // errorPolicy: 'all' preserves partial data when sibling fields error. The
  // backend's `ChipReseller.shippingAddress` is nullable but its sub-fields
  // (line1/city/state/country/zip) are non-null — so a reseller with an empty
  // address record triggers a non-null field error that, under the default
  // 'none' policy, would null out *all* of `data`, taking `isAdmin` and
  // `orgName` with it.
  const { data, loading: orgInfoLoading } = useQuery<RescueOrgInfoData>(rescueOrgInfoQuery, { errorPolicy: 'all' });
  const orgName = resolveOrgName(data);
  const orgId = resolveOrgId(data);
  const shippingAddress = data?.currentUser?.chipReseller?.shippingAddress ?? null;
  const isAdmin = data?.currentUser?.isAdmin ?? false;
  const isVerifiedReseller = data?.currentUser?.chipReseller?.verified ?? false;
  const isReseller = !!data?.currentUser?.chipReseller;

  // Identify as soon as the session is available, so portal events fired before
  // the org GraphQL query resolves still carry the user's email trait.
  useEffect(() => {
    if (session?.userId) {
      identifyUser(session.userId, { email: session.email });
    }
  }, [session?.userId, session?.email]);

  useEffect(() => {
    if (orgId && orgName) {
      setRescueAnalyticsContext(orgId, orgName);
      if (session?.userId) {
        identifyUser(session.userId, {
          email: session.email,
          company: { name: orgName, id: orgId },
        });
      }
      // RescueLogin (mounted outside this provider) sets a flag on successful
      // auth so we can fire the Login event here, after org context is set.
      if (sessionStorage.getItem(RESCUE_LOGIN_PENDING_TRACK_KEY)) {
        trackLogin();
        sessionStorage.removeItem(RESCUE_LOGIN_PENDING_TRACK_KEY);
      }
    }
  }, [orgId, orgName, session?.userId, session?.email]);

  return (
    <RescueContext.Provider
      value={{
        orgName,
        shippingAddress,
        isAdmin,
        sidebarOpen,
        setSidebarOpen,
        isVerifiedReseller,
        isReseller,
        orgInfoLoading,
      }}
    >
      {children}
    </RescueContext.Provider>
  );
}
